import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { fetchTenants, tenantKeys } from "@/lib/api/tenants";
import { useSwitchTenantHref, useTenant } from "@/lib/tenant";
import { cn } from "@/lib/utils";

/**
 * Which tenant this console is looking at, and how to move to another.
 *
 * Switching REWRITES ONLY THE /t/{slug} PREFIX of the current address and
 * keeps the query string. An operator comparing two tenants is on the same
 * screen with the same filters in both, which is the whole reason to switch;
 * dropping them to the overview on every switch would make the comparison a
 * manual re-navigation each time.
 *
 * A disabled tenant is LISTED and marked, not hidden. Its data is still
 * readable, and hiding it would make a tenant somebody deliberately disabled
 * look like one that was deleted.
 */
export function TenantSwitcher(): React.ReactElement {
  const navigate = useNavigate();
  const scope = useTenant();
  const hrefFor = useSwitchTenantHref();

  const tenants = useQuery({
    queryKey: tenantKeys.list(),
    queryFn: fetchTenants,
    staleTime: 60_000,
    retry: false,
  });

  const rows = tenants.data ?? [];
  const current = scope?.tenant ?? null;
  const label =
    current !== null
      ? current.display_name
      : tenants.isPending
        ? "Loading tenants..."
        : (scope?.slug ?? "No tenant");

  return (
    <div className="flex min-w-0 flex-col" data-testid="tenant-name">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            data-testid="tenant-switcher"
            aria-label="Switch tenant"
            className="-ml-2 h-7 max-w-[16rem] justify-start gap-1.5 px-2"
          >
            <span className="truncate text-sm font-semibold">{label}</span>
            {current !== null && !current.enabled ? (
              <span className="rounded border border-state-manual/40 px-1 text-[10px] uppercase tracking-wide text-state-manual">
                Disabled
              </span>
            ) : null}
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel>Tenants</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {tenants.isPending ? (
            <DropdownMenuItem disabled>Loading...</DropdownMenuItem>
          ) : tenants.isError ? (
            // No invented list. The header says the tenants could not be read,
            // which is a different thing from "there is only this one".
            <DropdownMenuItem disabled data-testid="tenant-switcher-error">
              The tenant list could not be read
            </DropdownMenuItem>
          ) : rows.length === 0 ? (
            <DropdownMenuItem disabled>No tenants are listed</DropdownMenuItem>
          ) : (
            rows.map((t) => (
              <DropdownMenuItem
                key={t.id}
                data-testid={`tenant-option-${t.slug}`}
                onSelect={() => {
                  void navigate(hrefFor(t.slug));
                }}
              >
                <Check
                  className={cn("h-4 w-4", t.slug === scope?.slug ? "opacity-100" : "opacity-0")}
                  aria-hidden="true"
                />
                <span className="truncate">{t.display_name}</span>
                {t.enabled ? null : (
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-state-manual">
                    Disabled
                  </span>
                )}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <span className="truncate text-[11px] text-ink-3">
        Microsoft 365 &amp; Entra ID inventory - read only
      </span>
    </div>
  );
}
